import {
  AEMGraphQLQuery,
  CabinI,
  CruiseI,
  Expression,
  GetBookingMyMscResponse,
  PassengerI,
  Port,
} from '@msc-b2c/interfaces/interfaces';
import {
  CabinCategoryMap,
  GetShipsResponse,
  MultiCruiseSelectedInfo,
} from '@msc-b2c/interfaces/types';
import {
  fetchPortListFromFragment,
  getActiveSession,
  replacePlaceholders,
} from '@msc-b2c/shared-utils';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';

/**
 * Get the device type based on the user agent
 * @returns {string} - The device type (mobile, tablet, or desktop)
 */
export const getDeviceType = (): string => {
  const ua = navigator.userAgent;

  if (/Tablet|iPad/i.test(ua)) {
    return 'tablet';
  } else if (/Mobi|Android/i.test(ua)) {
    return 'mobile';
  } else {
    return 'desktop';
  }
};

/**
 *
 * @param cabins array of cabin
 * @param passengerID passenger ID
 * @returns the cabin where the passeger ID is present
 */
export const getCabinByPassengerId = (
  cabins: CabinI[],
  passengerID: string
): CabinI | undefined => {
  return cabins.find((cabin) =>
    cabin.passengers?.some((passenger) => passenger.id === passengerID)
  );
};

export const getCabinByCruiseId = (cabins: CabinI[], cruiseId: string): CabinI | undefined => {
  return cabins.find((cabin) => cabin.cruise.id === cruiseId);
};

/**
 *
 * @param cabins array of cabin
 * @returns a [boolean, CabinI[]] where isMultiCruise indicating if there are multiple unique cruises and multicabins the belong to unique cruises
 */
export function getMultiCruise(cabins: CabinI[]): [boolean, CabinI[]] {
  const uniqueCruiseIds = new Set<string>();
  let multiCabins: CabinI[] = cabins.filter((cabin) => {
    const cruiseId = cabin.cruise.id;
    if (uniqueCruiseIds.has(cruiseId) || cabin.bookingStatus?.code === 'CANCELLED') {
      return false;
    }
    uniqueCruiseIds.add(cruiseId);
    return true;
  });

  let isMultiCruise = false;
  if (uniqueCruiseIds.size > 1) {
    multiCabins = multiCabins.filter((cabin) =>
      cabin.passengers?.some((passenger) => passenger.id === getActiveSession().passengerId)
    );

    isMultiCruise = true;
  }
  return [isMultiCruise, multiCabins];
}

/**
 *
 * Get all unique the cruise departure port code and arrival port code
 * Get all the information of the unique code port from AEM GraphQL Query
 * Using the response of the graphql to read the name of port to display in the dropdown
 *
 * @param cabins array of cabins
 * @param formatText select dropdown format
 * @param dateFormat date format
 * @returns list of {value,text} to display in the BaseSelect
 */
export async function getMultiCruiseSelect(
  cabins: CabinI[],
  formatText: string,
  dateFormat: string
): Promise<MultiCruiseSelectedInfo> {
  const codes: string[] = [];
  cabins.forEach((cabin) => {
    codes.push(cabin.cruise.departurePort.code, cabin.cruise.arrivalPort);
  });

  const uniqueCode = new Set(codes);
  const expressions: Expression[] = [];
  uniqueCode.forEach((singleCode) => {
    const ex: Expression = { value: singleCode };
    expressions.push(ex);
  });

  const pc: AEMGraphQLQuery = {
    _expressions: expressions,
    _logOp: 'OR',
  };

  const ports: Port[] | null = await fetchPortListFromFragment(pc);

  const selectItem: { value: string; text: string }[] = [];

  cabins.sort((a, b) => {
    const dateA = new Date(a.cruise.departureDate);
    const dateB = new Date(b.cruise.departureDate);
    return dateA.getTime() - dateB.getTime();
  });

  let isOngoingCruise = false;
  let isClosedCruise = false;

  cabins.forEach((cabin) => {
    const departureDate = cabin.cruise.departureDate;
    const arrivalDate = cabin.cruise.arrivalDate;
    isOngoingCruise = isCruiseOngoing(departureDate, arrivalDate);
    isClosedCruise = isCruiseClosed(departureDate, arrivalDate);
    if (!isClosedCruise) {
      if (!isOngoingCruise) {
        const departurePort: Port | undefined = ports?.find(
          (port) => port.portCode === cabin.cruise.departurePort.code
        );
        const arrivalPort: Port | undefined = ports?.find(
          (port) => port.portCode === cabin.cruise.arrivalPort
        );

        const departureCode = departurePort?.portName ?? cabin.cruise.departurePort.code;
        const arrivalCode = arrivalPort?.portName ?? cabin.cruise.arrivalPort;

        const item: { value: string; text: string } = {
          value: cabin.cruise.id,
          text: replacePlaceholders(formatText, {
            departureDate: dayjs(cabin.cruise.departureDate, 'YYYY-MM-DD').format(dateFormat),
            departureCode: departureCode,
            arrivalCode: arrivalCode,
          }),
        };
        selectItem.push(item);
      }
    }
  });
  return { items: selectItem, isClosedCruise: isClosedCruise, isOngoingCruise: isOngoingCruise };
}

/**
 *
 * @param passengerId passenger id
 * @param passengers all the passenger present in the booking
 * @returns only the passenger object where the passenger id is present
 */
export function getPassengerbyId(
  passengerId: string | undefined,
  passengers: PassengerI[] | undefined
): PassengerI | undefined {
  return passengers?.find((passenger) => passenger.id === passengerId);
}

/**
 *
 * @param departureDate
 * @param arrivalDate
 * @returns in base of the date if the cruise is ongoing o not
 */
export function isCruiseOngoing(departureDate: string, arrivalDate: string) {
  const today = dayjs().utc(true);
  dayjs.extend(isBetween);
  return dayjs(today).isBetween(departureDate, arrivalDate, 'day', '[]');
}

/**
 *
 * @param departureDate
 * @param arrivalDate
 * @returns in base of the date if the cruise is closed o not
 */
export function isCruiseClosed(departureDate: string, arrivalDate: string) {
  const today = dayjs().utc(true);
  return dayjs(today).isAfter(departureDate) && dayjs(today).isAfter(arrivalDate);
}

/**
 *
 * @param cruiseId
 * @param cabins
 * @returns return cabin of the cruiseId selected
 */
export function getDepartureDateByCruiseSelect(
  cruiseId: string,
  cabins: CabinI[]
): CabinI | undefined {
  return cabins.find((cabin) => cabin.cruise.id === cruiseId);
}

export function isMulticruise(booking: GetBookingMyMscResponse): {
  isMulticruise: boolean;
  cruises: CruiseI[];
} {
  const multicruiseItems = getFutureMulticruiseItems(booking);
  return { isMulticruise: multicruiseItems?.length > 1, cruises: multicruiseItems };
}

/**
 * Determines if there are multiple cabins for the specified cruise in the booking.
 *
 * @param booking - The booking object containing cabin information.
 * @param currentCruiseId - The cruise ID to check for multiple cabins.
 * @returns `true` if more than one cabin exists for the given cruise ID (excluding cancelled cabins), otherwise `false`.
 */
export function isMultiCabins(booking: GetBookingMyMscResponse, currentCruiseId: string): boolean {
  const cabins = booking?.cabins ?? [];
  // check if more than 1 cabin has the same cruise.id
  const cruiseIdCount: Record<string, number> = {};
  cabins.forEach((cabin) => {
    const cruiseId = cabin.cruise.id;
    if (cabin.bookingStatus?.code !== 'CANCELLED') {
      cruiseIdCount[cruiseId] = (cruiseIdCount[cruiseId] || 0) + 1;
    }
  });

  return cruiseIdCount[currentCruiseId] > 1;
}

/**
 * Retrieves a list of unique cruise objects from the provided booking data.
 *
 * This function ensures that only unique cruises are included in the result by
 * using a `Map` to track cruises based on their `id`. Each cruise is represented
 * by the first occurrence of its `id` in the `cabins` array.
 *
 * @param booking - The booking data containing an array of cabins.
 * @returns An array of unique cruise objects. If the booking is undefined or empty,
 *          an empty array is returned.
 */
export function getFutureMulticruiseItems(booking: GetBookingMyMscResponse): CruiseI[] {
  if (booking) {
    // Create a Map to avoid duplicates based on cruise.id.
    const uniqueCruisesMap = new Map();

    booking?.cabins.forEach((cabin) => {
      const cruiseId = cabin.cruise.id;

      //check if the cabin.cruise.departureDate is in the future
      //const today = dayjs().utc(true);
      const isFutureCruise = dayjs()
        .startOf('day')
        .isBefore(dayjs(cabin.cruise.departureDate).startOf('day'));

      if (
        !uniqueCruisesMap.has(cruiseId) &&
        cabin.bookingStatus?.code !== 'CANCELLED' &&
        isFutureCruise &&
        cabin.passengers?.find((pax) => pax.id === getActiveSession().passengerId)
      ) {
        // Map.set(cruiseId, cruiseObj) saves only the first cruise object with a given ID.
        uniqueCruisesMap.set(cruiseId, cabin.cruise);
      }
    });

    // Finally, Array.from(...values()) returns an array of unique cruise objects.
    const multicruises = Array.from(uniqueCruisesMap.values());
    // Sort the cruises by departure date
    multicruises.sort((a, b) => {
      return new Date(a.departureDate).getTime() - new Date(b.departureDate).getTime();
    });

    return multicruises;
  }
  return [];
}

/**
 * Maps cabin categories from the provided ship data to their respective cabin codes.
 *
 * This function organizes cabin codes into categories such as interior, ocean view, balcony, suite, and yacht club.
 * If a category is undefined or empty, it defaults to an empty array.
 *
 * @param {GetShipsResponse} shipData - The data object containing cabin category information for a ship.
 * @returns {CabinCategoryMap} - An object mapping cabin categories to arrays of cabin codes.
 * The keys in the returned object are:
 * - `INS`: Interior cabin codes.
 * - `OUT`: Ocean view cabin codes.
 * - `BAL`: Balcony cabin codes.
 * - `SUI`: Suite cabin codes.
 * - `YTC`: Yacht club cabin codes.
 */
export function mapCabinCategories(shipData: GetShipsResponse | null): CabinCategoryMap {
  return {
    INS: shipData?.interiorCategoryCf?.map((cabin) => cabin.cabinCode) ?? [],
    OUT: shipData?.oceanViewCategoryCf?.map((cabin) => cabin.cabinCode) ?? [],
    BAL: shipData?.balconyCategoryCf?.map((cabin) => cabin.cabinCode) ?? [],
    SUI: shipData?.suiteCategoryCf?.map((cabin) => cabin.cabinCode) ?? [],
    YTC: shipData?.yachtClubCategoryCf?.map((cabin) => cabin.cabinCode) ?? [],
  };
}

/**
 * Returns the active passenger id from booking, preferring the lead passenger if available.
 * @param booking Booking object
 */
export function getActivePassengerId(booking: GetBookingMyMscResponse): string | undefined {
  if (booking && Array.isArray(booking.passengers) && booking.passengers.length > 0) {
    const lead = booking.passengers.find((p: any) => p.isLeadPassenger);
    return lead ? lead.id : booking.passengers[0].id;
  }
  return undefined;
}

/**
 * Returns an array of booked excursion codes for the active passenger in the booking.
 * @param booking Booking object
 */
export function getBookedExcursionCodesForActivePassenger(
  booking: GetBookingMyMscResponse
): string[] {
  const activePassengerId = getActivePassengerId(booking);
  const activeCruiseId = getActiveSession().cruiseId;
  const codes: string[] = [];
  if (Array.isArray(booking?.prepaid?.bookings)) {
    booking.prepaid.bookings.forEach((prepaidBooking: any) => {
      if (activeCruiseId && prepaidBooking.cruise?.id !== activeCruiseId) {
        return;
      }
      if (Array.isArray(prepaidBooking.excursions)) {
        prepaidBooking.excursions.forEach((exc: any) => {
          if (!activePassengerId || !exc.passenger || exc.passenger.id === activePassengerId) {
            if (exc.code) codes.push(exc.code);
          }
        });
      }
    });
  }
  return codes;
}
